// CampaignsAIReview.jsx — weekly AI review panel for the Campaigns page.
// Fetches /api/ai/campaigns-review and renders verdict cards per campaign:
//   • status badge (winning/mixed/losing/too_early)
//   • clear recommendation pill (extend/stop/boost/wait/adjust_price)
//   • short Arabic reasoning + the single data-point AI used
//   • imperative "next action" the user should take
import React, { useState } from "react";
import { api } from "./lib/api";

const STATUS_META = {
  winning:   { ar: "🏆 ناجحة",     cls: "rev-st-win"  },
  mixed:     { ar: "⚖ متوسطة",     cls: "rev-st-mix"  },
  losing:    { ar: "❌ خاسرة",     cls: "rev-st-lose" },
  too_early: { ar: "⏳ مبكّر للحكم", cls: "rev-st-early"},
};

const REC_META = {
  extend:        { ar: "تمديد",        cls: "rev-rec-extend" },
  stop:          { ar: "إيقاف فوري",   cls: "rev-rec-stop"   },
  boost:         { ar: "زيادة التركيز", cls: "rev-rec-boost"  },
  wait:          { ar: "انتظار 7 أيام", cls: "rev-rec-wait"   },
  adjust_price:  { ar: "تعديل السعر",  cls: "rev-rec-adjust" },
};

export default function CampaignsAIReview() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.post("/ai/campaigns-review");
      setData(d);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "فشل تشغيل المراجعة";
      setError(typeof msg === "string" ? msg : "خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card camp-review-panel" data-testid="campaigns-ai-review">
      <div className="camp-review-head">
        <div>
          <div className="camp-review-title">🔔 مراجعة AI الأسبوعية للحملات</div>
          <div className="camp-review-sub">
            تحليل كل حملة نشطة ومقارنة مبيعات منتجاتها بالبيانات الفعلية — مع توصية محددة لكل واحدة.
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={run}
          disabled={loading}
          data-testid="camp-review-run"
        >
          {loading ? "⏳ جارٍ التحليل…" : data ? "🔄 شغّل المراجعة مرة أخرى" : "✨ شغّل المراجعة"}
        </button>
      </div>

      {error && <div className="camp-review-error" data-testid="camp-review-error">⚠ {error}</div>}

      {data && data.summary && (
        <div className="camp-review-summary" data-testid="camp-review-summary">
          <div className="camp-review-headline">{data.summary.headline}</div>
          <div className="camp-review-pillbar">
            <span className="camp-rev-pill rev-st-win">🏆 ناجحة · {data.summary.winning || 0}</span>
            <span className="camp-rev-pill rev-st-mix">⚖ متوسطة · {data.summary.mixed || 0}</span>
            <span className="camp-rev-pill rev-st-lose">❌ خاسرة · {data.summary.losing || 0}</span>
            <span className="camp-rev-pill rev-st-early">⏳ مبكّر · {data.summary.too_early || 0}</span>
          </div>
        </div>
      )}

      {data && Array.isArray(data.reviews) && data.reviews.length > 0 && (
        <div className="camp-review-grid" data-testid="camp-review-list">
          {data.reviews.map((r, i) => {
            const sm = STATUS_META[r.status] || STATUS_META.too_early;
            const rm = REC_META[r.recommendation] || REC_META.wait;
            return (
              <div key={r.campaign_id || i} className="camp-review-card" data-testid={`camp-review-card-${i}`}>
                <div className="camp-review-card-head">
                  <span className={`camp-rev-status ${sm.cls}`}>{sm.ar}</span>
                  <span className={`camp-rev-rec ${rm.cls}`}>{r.recommendation_label_ar || rm.ar}</span>
                </div>
                <div className="camp-review-name">{r.name}</div>
                <div className="camp-review-reason">{r.reasoning_ar}</div>
                {r.key_metric && (
                  <div className="camp-review-metric">
                    <span className="kr-l">{r.key_metric.label}</span>
                    <span className="kr-v">
                      {r.key_metric.value}
                      {r.key_metric.trend === "up" ? " ▲" : r.key_metric.trend === "down" ? " ▼" : ""}
                    </span>
                  </div>
                )}
                {r.next_action && (
                  <div className="camp-review-action">
                    <span className="ca-icon">🎯</span>
                    <span>{r.next_action}</span>
                  </div>
                )}
                {typeof r.score_0_100 === "number" && (
                  <div className="camp-review-score">
                    <div className="cs-bar"><div className="cs-fill" style={{ width: `${Math.min(100, r.score_0_100)}%` }} /></div>
                    <div className="cs-label">ثقة الحُكم: {r.score_0_100}%</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && !loading && (!data.reviews || data.reviews.length === 0) && (
        <div className="camp-review-empty" data-testid="camp-review-empty">
          {data.summary?.headline || "لا توجد حملات نشطة للمراجعة. أنشئ حملة كومبو أولاً من محاكي الكومبو."}
        </div>
      )}
    </div>
  );
}
